import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  GraduationCap, Plus, Play, BookOpen, Users, ShieldCheck, Trophy,
  Clock, ArrowRight, Sparkles, Lightbulb, Lock,
} from "lucide-react";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, SectionCard, PillTabs, StatusBadge, EmptyState,
} from "@/components/app/PageKit";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

const COURSES = [
  { id: 1, title: "Onboarding Essentials",   cat: "Compliance",   duration: "2h",   level: "All",          enrolled: 49, rating: 4.8, hue: "from-violet-500 to-fuchsia-500", instructor: "Priya Sharma", required: true },
  { id: 2, title: "Effective Manager 101",   cat: "Leadership",   duration: "4h 30m", level: "Manager",   enrolled: 14, rating: 4.6, hue: "from-blue-500 to-cyan-500",       instructor: "Arjun Mehta",  required: false },
  { id: 3, title: "POSH Awareness",          cat: "Compliance",   duration: "1h",   level: "All",          enrolled: 49, rating: 4.9, hue: "from-rose-500 to-orange-500",     instructor: "HR Team",      required: true },
  { id: 4, title: "Design Systems Bootcamp", cat: "Skill",        duration: "6h",   level: "Designer",     enrolled: 5,  rating: 4.7, hue: "from-emerald-500 to-teal-500",    instructor: "Vikram Singh", required: false },
  { id: 5, title: "Public Speaking",         cat: "Soft Skill",   duration: "3h",   level: "All",          enrolled: 22, rating: 4.5, hue: "from-amber-500 to-orange-500",    instructor: "Anjali Desai", required: false },
  { id: 6, title: "Advanced React Patterns", cat: "Skill",        duration: "8h",   level: "Engineer",     enrolled: 9,  rating: 4.9, hue: "from-fuchsia-500 to-pink-500",    instructor: "Sneha Iyer",   required: false },
];

const ASSIGNED = [
  { id: "a1", course: "Onboarding Essentials", progress: 75, due: "May 12", status: "in_progress" },
  { id: "a2", course: "POSH Awareness",        progress: 100, due: "Apr 30", status: "completed" },
  { id: "a3", course: "Effective Manager 101", progress: 30,  due: "May 25", status: "in_progress" },
];

const PATHS = [
  { id: "p1", title: "New Hire Path",       courses: 4, learners: 8,  pct: 60 },
  { id: "p2", title: "Manager Track",       courses: 6, learners: 12, pct: 35 },
  { id: "p3", title: "Engineer Excellence", courses: 8, learners: 14, pct: 78 },
];

export default function LMS() {
  const [tab, setTab] = useState("catalog");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");

  const filtered = COURSES.filter(c => {
    if (search && !c.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (cat && c.cat !== cat) return false;
    return true;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="lms-page">
      <PageHeader
        category="LEARNING & DEVELOPMENT"
        title="Learning Hub"
        badge="Basic Version"
        description="Course catalog, compliance training, learning paths, and skill-building — designed to grow your people."
        primaryAction={{ label: "Assign Course",    icon: Plus,        onClick: () => {} }}
        secondaryAction={{ label: "Create Course",  icon: BookOpen,    onClick: () => {} }}
        moreActions={[
          { label: "Compliance Audit",    icon: ShieldCheck, onClick: () => {} },
          { label: "Learning Paths",      icon: Sparkles,    onClick: () => setTab("paths") },
        ]}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={BookOpen}   tone="violet"   label="Active Courses"   value={COURSES.length}                                sub="In catalog"          testid="lms-kpi-courses" />
        <KPICard icon={Users}      tone="blue"     label="Enrolled Learners" value={COURSES.reduce((a, c) => a + c.enrolled, 0)} sub="Across courses"      testid="lms-kpi-learners" />
        <KPICard icon={Trophy}     tone="emerald"  label="Completed (30d)"  value={42}                                            sub="Certifications earned" testid="lms-kpi-completed" />
        <KPICard icon={ShieldCheck} tone="amber"   label="Compliance"       value="92%"                                           sub="On-track"             testid="lms-kpi-compliance" />
        <KPICard icon={Clock}      tone="rose"     label="Overdue"          value={3}                                             sub="Need follow-up"       testid="lms-kpi-overdue" />
      </KPIGrid>

      <PillTabs
        value={tab} onChange={setTab}
        tabs={[
          { value: "catalog",    label: "Course Catalog",     badge: COURSES.length },
          { value: "assigned",   label: "My Learning",         badge: ASSIGNED.length },
          { value: "compliance", label: "Compliance" },
          { value: "paths",      label: "Learning Paths",      badge: PATHS.length },
        ]}
        testid="lms-tabs"
      />

      <FilterBar
        search={search} onSearch={setSearch}
        searchPlaceholder="Search courses, instructors, topics…"
        filters={[
          { label: "All Categories", value: cat, onChange: setCat, options: ["Compliance","Leadership","Skill","Soft Skill"] },
          { label: "All Levels",     onChange: () => {}, options: ["All","Engineer","Manager","Designer"] },
          { label: "All Durations",  onChange: () => {}, options: ["< 1h","1-3h","3-6h","6h+"] },
        ]}
      />

      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0">
          {tab === "catalog" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
              {filtered.map((c) => (
                <Card key={c.id} className="overflow-hidden border-slate-200 shadow-sm bg-white hover:shadow-md hover:-translate-y-0.5 transition-all min-w-0" data-testid={`course-${c.id}`}>
                  {/* Visual header */}
                  <div className={`relative h-24 bg-gradient-to-br ${c.hue} p-4 flex items-end justify-between`}>
                    {c.required && <Badge className="absolute top-3 right-3 bg-white/20 text-white border-0 text-[9px] font-bold uppercase backdrop-blur-sm">Required</Badge>}
                    <Badge className="bg-white/20 text-white border-0 text-[9px] font-bold uppercase backdrop-blur-sm">{c.cat}</Badge>
                    <span className="text-white/90 text-[10px] font-bold inline-flex items-center gap-1"><Clock className="w-3 h-3" />{c.duration}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-[14px] font-bold font-[Outfit] text-slate-900 line-clamp-2 leading-tight">{c.title}</h3>
                    <div className="text-[11px] text-slate-500 mt-1.5">By {c.instructor} · {c.level}</div>
                    <div className="mt-3 flex items-center justify-between text-[11px]">
                      <span className="inline-flex items-center gap-1 text-slate-500"><Users className="w-3 h-3" />{c.enrolled}</span>
                      <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">★ {c.rating}</span>
                    </div>
                    <Button size="sm" className="mt-3 w-full qh-grad-bg text-white font-semibold text-[12px]">
                      <Play className="w-3 h-3 mr-1" /> Start Course
                    </Button>
                  </div>
                </Card>
              ))}
              {filtered.length === 0 && <div className="col-span-full"><EmptyState icon={BookOpen} title="No courses match your filters" message="Try clearing the search or category." /></div>}
            </div>
          )}

          {tab === "assigned" && (
            <SectionCard icon={Trophy} title="My Learning" sub={`${ASSIGNED.length} assigned`}>
              <div className="space-y-2">
                {ASSIGNED.map((a) => {
                  const c = COURSES.find(c => c.title === a.course);
                  return (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-violet-200 transition" data-testid={`assigned-${a.id}`}>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c?.hue || "from-violet-500 to-fuchsia-500"} flex items-center justify-center text-white shrink-0`}>
                        <BookOpen className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-bold text-slate-900 truncate">{a.course}</span>
                          <StatusBadge status={a.status} />
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Due {a.due}</div>
                        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${a.progress}%` }} />
                        </div>
                      </div>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                        {a.status === "completed" ? "Review" : "Continue"} <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {tab === "compliance" && (
            <SectionCard icon={ShieldCheck} title="Compliance Status" sub="Mandatory training across the org">
              <div className="space-y-3">
                {COURSES.filter(c => c.required).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
                    <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-slate-900 truncate">{c.title}</div>
                      <div className="text-[11px] text-slate-500">{c.duration} · {c.cat}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[12px] font-bold text-emerald-700 tabular-nums">42 / 49</div>
                      <div className="text-[10px] text-slate-500">86% completed</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {tab === "paths" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {PATHS.map(p => (
                <Card key={p.id} className="p-5 border-slate-200 shadow-sm hover:shadow-md transition" data-testid={`path-${p.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
                    <div>
                      <h3 className="text-base font-bold font-[Outfit] text-slate-900">{p.title}</h3>
                      <div className="text-[11px] text-slate-500">{p.courses} courses · {p.learners} learners</div>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${p.pct}%` }} />
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">{p.pct}% average completion</div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right rail */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Trophy} title="Top Learners" sub="This month">
            <div className="space-y-2.5">
              {[
                { name: "Riya Malhotra",  hours: 14, badge: "🥇" },
                { name: "Sneha Iyer",     hours: 11, badge: "🥈" },
                { name: "Karan Joshi",    hours: 8,  badge: "🥉" },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Avatar className="w-8 h-8 ring-2 ring-white"><AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(l.name)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-slate-900 truncate">{l.name}</div>
                    <div className="text-[10px] text-slate-500">{l.hours}h learned</div>
                  </div>
                  <span className="text-base">{l.badge}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Lightbulb} title="Recommended" sub="Based on your role">
            <div className="space-y-2">
              {COURSES.slice(0, 3).map((c) => (
                <div key={c.id} className="p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer">
                  <div className="text-[12px] font-semibold text-slate-900 truncate">{c.title}</div>
                  <div className="text-[10px] text-slate-500">{c.duration} · {c.level}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}
